import React, { useState } from 'react';

export function AppUsageInstructions({ textColor }) {
  const listColor = textColor || 'var(--text-mid, #52627a)';
  return (
    <div className="app-usage-instructions" style={{ textAlign: 'left', ...(textColor ? { color: textColor } : {}) }}>
      <h3 style={{ marginBottom: 12 }}>How Train Daily Works</h3>

      <div className="usage-section">
        <h4 style={{ fontSize: '0.95rem', marginBottom: 6 }}>
          <i className="fas fa-futbol" style={{ color: 'var(--orange)', marginRight: 6 }}></i>
          Daily Training Sessions
        </h4>
        <ul style={{ margin: '0 0 16px', paddingLeft: 20, lineHeight: 1.8, fontSize: '0.9rem', color: listColor }}>
          <li>Each day, your player gets a fresh set of soccer training exercises</li>
          <li>Exercises are tailored to their age group (Under 10, 10-13, or 14+)</li>
          <li>They watch a video demo, then do the exercise and mark it complete</li>
          <li>Completing all exercises in a day earns <strong>points</strong></li>
        </ul>
      </div>

      <div className="usage-section">
        <h4 style={{ fontSize: '0.95rem', marginBottom: 6 }}>
          <i className="fas fa-fire" style={{ color: 'var(--orange)', marginRight: 6 }}></i>
          Streaks, Points & Levels
        </h4>
        <ul style={{ margin: '0 0 16px', paddingLeft: 20, lineHeight: 1.8, fontSize: '0.9rem', color: listColor }}>
          <li>Training on consecutive days builds a <strong>streak</strong> &mdash; the longer the streak, the better!</li>
          <li>Points unlock new <strong>levels</strong> (Infantil, Neymar, Mbappe, Salah, and more)</li>
          <li>Players can also earn <strong>badges</strong> for special achievements</li>
        </ul>
      </div>

      <div className="usage-section">
        <h4 style={{ fontSize: '0.95rem', marginBottom: 6 }}>
          <i className="fas fa-user-gear" style={{ color: 'var(--orange)', marginRight: 6 }}></i>
          Parent Dashboard
        </h4>
        <ul style={{ margin: '0 0 16px', paddingLeft: 20, lineHeight: 1.8, fontSize: '0.9rem', color: listColor }}>
          <li>Log in at <strong>{window.location.origin}/parent-login</strong></li>
          <li>View each player's <strong>streak, completions, points, level, and badges</strong></li>
          <li><strong>Reset PIN or password</strong> if your player forgets it</li>
          <li><strong>Add more players</strong> (up to 8 on a Family plan)</li>
          <li><strong>Manage billing</strong> &mdash; update payment, upgrade, or cancel anytime</li>
        </ul>
      </div>
    </div>
  );
}

export default function PwaInstall({ onDone, doneLabel = 'Done', showUsageInstructions = false }) {
  const [copied, setCopied] = useState(false);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent) && !/CriOS/.test(navigator.userAgent);
  const isIOSSafari = isIOS && isSafari;
  const isIOSNotSafari = isIOS && !isSafari;
  const isAndroid = /Android/.test(navigator.userAgent);
  const appUrl = window.location.origin;

  const handleCopy = () => {
    navigator.clipboard.writeText(appUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="pwa-install">
      <h3 style={{ marginBottom: 8 }}>Install Train Daily</h3>
      <p style={{ color: 'var(--text-mid, #52627a)', marginBottom: 20, fontSize: '0.95rem' }}>
        Add Train Daily to your home screen so your kid can open it like a regular app &mdash; no app store needed.
      </p>

      {isIOSSafari && (
        <div className="pwa-steps">
          <div className="pwa-step">
            <div className="pwa-step-num">1</div>
            <div>Tap the <strong>Share</strong> button <i className="fas fa-arrow-up-from-bracket" style={{ color: '#007AFF' }}></i> at the bottom of Safari (it's the square with an arrow pointing up)</div>
          </div>
          <div className="pwa-step">
            <div className="pwa-step-num">2</div>
            <div>Scroll down in the share menu and tap <strong>"Add to Home Screen"</strong> (it has a <strong>+</strong> icon)</div>
          </div>
          <div className="pwa-step">
            <div className="pwa-step-num">3</div>
            <div>Tap <strong>"Add"</strong> in the upper right corner &mdash; the Train Daily icon will appear on your home screen</div>
          </div>
          <div className="pwa-step">
            <div className="pwa-step-num">4</div>
            <div>Open the app from your home screen &mdash; it will run in full screen just like a regular app</div>
          </div>
          <div className="pwa-step">
            <div className="pwa-step-num"><i className="fas fa-check" style={{ color: '#1eae5a' }}></i></div>
            <div>Train Daily is now installed! Your kid can tap the icon to start training each day.</div>
          </div>
        </div>
      )}

      {isIOSNotSafari && (
        <div className="pwa-platform-note">
          <div style={{ marginBottom: 12 }}>
            <i className="fas fa-exclamation-triangle" style={{ color: '#f77c00', marginRight: 8 }}></i>
            <strong>You need Safari to install the app on iPhone/iPad.</strong>
          </div>
          <p style={{ marginBottom: 8 }}>On iOS, only Safari supports adding apps to the home screen. Here's what to do:</p>
          <ol style={{ margin: '0 0 12px', paddingLeft: 20, lineHeight: 1.8, fontSize: '0.9rem' }}>
            <li>Copy this link:</li>
          </ol>
          <div className="pwa-copy-row">
            <code className="pwa-url">{appUrl}</code>
            <button className="btn btn-sm btn-outline" onClick={handleCopy} type="button">
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
          <ol start={2} style={{ margin: '12px 0 0', paddingLeft: 20, lineHeight: 1.8, fontSize: '0.9rem' }}>
            <li>Open <strong>Safari</strong> and paste the link in the address bar</li>
            <li>Tap the <strong>Share</strong> button <i className="fas fa-arrow-up-from-bracket" style={{ color: '#007AFF' }}></i> at the bottom</li>
            <li>Tap <strong>"Add to Home Screen"</strong></li>
            <li>Tap <strong>"Add"</strong> &mdash; done!</li>
          </ol>
        </div>
      )}

      {isAndroid && (
        <div className="pwa-steps">
          <div className="pwa-step">
            <div className="pwa-step-num">1</div>
            <div>Open this page in <strong>Chrome</strong>, <strong>Edge</strong>, or your default browser</div>
          </div>
          <div className="pwa-step">
            <div className="pwa-step-num">2</div>
            <div>Tap the <strong>three-dot menu</strong> <strong>&#8942;</strong> in the top-right corner</div>
          </div>
          <div className="pwa-step">
            <div className="pwa-step-num">3</div>
            <div>Tap <strong>"Install app"</strong> or <strong>"Add to Home Screen"</strong> (the wording varies by browser)</div>
          </div>
          <div className="pwa-step">
            <div className="pwa-step-num">4</div>
            <div>Confirm by tapping <strong>"Install"</strong> &mdash; the Train Daily icon will appear on your home screen</div>
          </div>
          <div className="pwa-step">
            <div className="pwa-step-num"><i className="fas fa-check" style={{ color: '#1eae5a' }}></i></div>
            <div>Train Daily is now installed! Your kid can tap the icon to start training each day.</div>
          </div>
        </div>
      )}

      {!isIOS && !isAndroid && (
        <div className="pwa-platform-note">
          <p style={{ marginBottom: 12 }}>
            <strong>On your phone (recommended):</strong> Visit <strong>{appUrl}</strong> in your phone's browser and follow the prompts to add it to your home screen. It works best as a home screen app on a phone or tablet.
          </p>
          <p style={{ marginBottom: 12 }}>
            <strong>On desktop (Chrome/Edge):</strong> Look for the install icon <i className="fas fa-download" style={{ color: 'var(--orange)' }}></i> in the address bar, or go to the browser menu and select "Install Train Daily."
          </p>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-mid, #52627a)' }}>
            You can also bookmark this page for quick access from any device.
          </p>
        </div>
      )}

      {showUsageInstructions && (
        <div style={{ marginTop: 28, borderTop: '1px solid var(--card-border, #e2e8f0)', paddingTop: 24 }}>
          <AppUsageInstructions />
        </div>
      )}

      {onDone && (
        <button className="btn btn-primary order-btn" onClick={onDone} style={{ marginTop: 24, width: '100%' }} type="button">
          {doneLabel}
        </button>
      )}
    </div>
  );
}
